import { useState } from 'react'
import {
  Space,
  Typography,
  Tooltip,
  Row,
  Col,
  Form,
  notification,
  Popconfirm,
  Checkbox,
} from 'antd'
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAsync } from 'react-use'
import _ from 'lodash'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import {
  getProfileListApi,
  createProfileApi,
  getProfileByIdApi,
  deleteProfileByIdApi,
  updateProfileByIdApi,
} from 'services/profile'

import Role from './components/Role'
import Setup from './components/Setup'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function PageProfile () {
  const { t } = useTranslate()

  const [form] = Form.useForm()

  const [dataSource, setDataSource] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  // const [deletingId, setDeletingId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getProfileListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setDataSource(res.data?.items)
  }

  const _create = async (values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await createProfileApi({
      profile_name: values.name,
      profile_description: values.description,
      role_id: values.role,
      is_active: values.status,
      menu_id: values.setup,
      is_assignable: values.isAssignable,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _update = async (id, values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await updateProfileByIdApi({
      id,
      profile_name: values.name,
      profile_description: values.description,
      role_id: values.role,
      is_active: values.status,
      menu_id: values.setup,
      is_assignable: values.isAssignable,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteProfileByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      sorter: (a, b) => a.status.label.localeCompare(b.status.label),
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Assignable'),
      dataIndex: 'isAssignable',
      key: 'isAssignable',
      sorter: (a, b) => `${a.isAssignable}`.localeCompare(`${b.isAssignable}`),
      render: (text, record, index) => {
        return <Typography.Text>{_.toString(text)}</Typography.Text>
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={async () => {
                  setEditingId(record?.id)

                  setShowCreateModal(true)

                  const res = await getProfileByIdApi(record?.id)

                  if (res.status === 'error') {
                    notification.error({
                      message: 'Failed',
                      description: res.message,
                    })

                    return
                  }

                  const editingDataSource = res.data

                  form.setFieldsValue({
                    name: editingDataSource?.profile_name,
                    description: editingDataSource?.profile_description,
                    role: editingDataSource?.role_id,
                    setup: _.map(editingDataSource?.menus, (menu) => {
                      return menu?.menu_id
                    }),
                    status: editingDataSource?.is_active,
                    isAssignable: editingDataSource?.is_assignable,
                  })
                }}
              />
            </Tooltip>
            <Popconfirm
              title={t('Are you sure to delete this task?')}
              okText={t('Yes')}
              cancelText={t('No')}
              placement='bottomRight'
              onConfirm={async () => {
                _delete(record?.id)
              }}
            >
              <Tooltip title={t('Delete')}>
                <Button type='link' icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-profile'>
      <Page
        title={t('Profile')}
        searchPlaceholder={t('Search Incident Topic')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
            page: 1,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col></Col>
            <Col>
              <Button type='primary' size='large' onClick={() => setShowCreateModal(true)}>
                {t('Create New Profile')}
              </Button>
            </Col>
          </Row>
          <Card title={t('Profile')}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={editingId ? t('Update Profile') : t('Create New Profile')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            loading={submitting}
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {editingId ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              status: true,
            }}
            onFinish={async (values) => {
              if (editingId) {
                _update(editingId, values)
              } else {
                _create(values)
              }
            }}
          >
            <Form.Item label={t('Profile Name')} name='name' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label={t('Profile Description')} name='description'>
              <Input />
            </Form.Item>

            <Form.Item label={t('Role')} name='role'>
              <Role />
            </Form.Item>

            <Form.Item label={t('Setup')} name='setup'>
              <Setup />
            </Form.Item>

            <Form.Item valuePropName='checked' label={t('Assignable')} name='isAssignable'>
              <Checkbox />
            </Form.Item>

            <Row justify='space-between'>
              <Col></Col>
              <Col>
                <Form.Item name='status'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
